// 판정이 일어나는 상태 경계에 클립 타격 프레임을 고정한다. 시간 단위는 초(입력 tick은 유효 세계 틱).
import { BOSS_TIMING, KNOCKBACK, MOBS, MOB_STATS } from '../../shared/config';
import { BossState, MobKind, MobState } from '../../shared/types';
import { clipFrame, type SkinAtlas } from './skinning';

const progress = (tick: number, duration: number): number => Math.max(0, Math.min(1, tick / duration));
export const MOB_CLIPS = ['idle', 'run', 'attack', 'hit', 'death', 'spawn'] as const;
export const BOAR_CLIPS = ['idle', 'run', 'windup', 'charge', 'hit', 'death', 'spawn'] as const;
export const BOSS_CLIPS = ['idle', 'walk', 'intro', 'slam', 'sweep', 'summon', 'stagger', 'death'] as const;

/** windup: 이번 선딜 길이(틱). 게임이 공격을 발동하는 틱과 같아야 선딜 자세가 끝까지 재생된다(c018 지역 공세로 짧아짐) */
export function mobFrame(atlas: SkinAtlas, kind: number, state: number, tick: number, cycle: number, windup = MOB_STATS[kind].windup): number {
  const stats = MOB_STATS[kind];
  const boar = kind === MobKind.Boar;
  const impact = kind === MobKind.ShieldGoblin ? 0.93 : 0.8;
  switch (state) {
    case MobState.Spawning: return clipFrame(atlas, 'spawn', Math.min(1.5, atlas.clips.spawn.duration) * progress(tick, MOBS.spawnTicks));
    case MobState.Chase: return clipFrame(atlas, 'run', cycle, true);
    case MobState.Windup: return boar ? clipFrame(atlas, 'windup', atlas.clips.windup.duration * progress(tick, windup))
      : clipFrame(atlas, 'attack', 0.3 + (impact - 0.3) * progress(tick, windup));
    case MobState.Attack: return boar ? clipFrame(atlas, 'charge', cycle, true)
      : clipFrame(atlas, 'attack', impact + (atlas.clips.attack.duration - impact) * progress(tick, stats.attackTicks));
    case MobState.Stunned: return clipFrame(atlas, 'hit', 0.5 * progress(tick, KNOCKBACK.normalStun));
    case MobState.Airborne: return clipFrame(atlas, 'hit', Math.min(0.5, tick / 60));
    case MobState.Dying: return clipFrame(atlas, 'death', atlas.clips.death.duration * progress(tick, MOBS.dyingTicks));
    default: return clipFrame(atlas, 'idle', cycle, true);
  }
}

export function bossFrame(atlas: SkinAtlas, state: number, tick: number, cycle: number): number {
  switch (state) {
    case BossState.Intro: return clipFrame(atlas, 'intro', 0.9 + 1.5 * progress(tick, BOSS_TIMING.intro));
    case BossState.Chase: return clipFrame(atlas, 'walk', cycle, true);
    // 피해는 Slam 진입 때 발생한다. 착지(2.1초)를 선딜 끝에 놓고 2.3초까지 후딜을 재생한다.
    case BossState.SlamWindup: return clipFrame(atlas, 'slam', 0.6 + 1.5 * progress(tick, BOSS_TIMING.slamWindup));
    case BossState.Slam: return clipFrame(atlas, 'slam', 2.1 + 0.2 * progress(tick, BOSS_TIMING.strike));
    case BossState.SweepWindup: return clipFrame(atlas, 'sweep', 0.3 + 0.73 * progress(tick, BOSS_TIMING.sweepWindup));
    case BossState.Sweep: return clipFrame(atlas, 'sweep', 1.03 + (atlas.clips.sweep.duration - 1.03) * progress(tick, BOSS_TIMING.strike));
    case BossState.Summon: return clipFrame(atlas, 'summon', 0.8 * progress(tick, BOSS_TIMING.summon));
    case BossState.Stagger: return clipFrame(atlas, 'stagger', atlas.clips.stagger.duration * progress(tick, BOSS_TIMING.stagger));
    case BossState.Dying: return clipFrame(atlas, 'death', atlas.clips.death.duration * progress(tick, BOSS_TIMING.dying));
    default: return clipFrame(atlas, 'idle', cycle, true);
  }
}

/** 슬롯 세대 변경·일시정지·LOD 교체에 독립적인 두 자세 전환. 모든 배열은 초기화 때 할당한다. */
export class PoseBlend {
  readonly a: Float32Array;
  readonly b: Float32Array;
  readonly mix: Float32Array;
  private readonly state: Int16Array;
  private readonly since: Float64Array;
  constructor(count: number) {
    this.a = new Float32Array(count); this.b = new Float32Array(count); this.mix = new Float32Array(count);
    this.state = new Int16Array(count).fill(-1); this.since = new Float64Array(count);
  }
  reset(slot = -1): void { if (slot < 0) this.state.fill(-1); else this.state[slot] = -1; }
  update(slot: number, state: number, frame: number, worldNow: number, continuous = false): void {
    if (this.state[slot] < 0 || worldNow < this.since[slot]) {
      this.a[slot] = frame; this.since[slot] = worldNow - 7.2;
    } else if (this.state[slot] !== state) {
      this.a[slot] = this.mix[slot] < 0.5 ? this.a[slot] : this.b[slot];
      this.since[slot] = worldNow - (continuous ? 7.2 : 0);
    }
    this.state[slot] = state; this.b[slot] = frame;
    this.mix[slot] = progress(worldNow - this.since[slot], 7.2); // 0.12초
    if (this.mix[slot] >= 1) this.a[slot] = frame;
  }
}
